import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../Services/axiosInstance';
import Swal from 'sweetalert2';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  Box,
  VStack,
  Text,
  Checkbox,
} from '@chakra-ui/react';
import PrimaryButton from '../../components/themeComponents/PrimaryButton';
import SecondaryButton from '../../components/themeComponents/SecondaryButton';

export default function OrgSettings() {
  const { orgToken } = useParams();
  const [settings, setSettings] = useState([]);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [newSetting, setNewSetting] = useState({ key: '', value: '', data_type: '' });
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure();

  const dataTypes = {
    "string": 'text',
    "int": 'number',
    "long": 'number',
    "double": 'number',
    "float": 'number',
    "boolean": 'checkbox',
    "timestamp": 'datetime-local',
    "date": 'date',
    "time": 'time',
    "json": 'text'
  };

  const getFeatures = async () => {
    try {
      let url = `/su/orgsettings/${orgToken}`;
      let response = await axiosInstance.get(url);
      setSettings(response.data.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  useEffect(() => {
    getFeatures();
  }, []);

  const handleEdit = (setting) => {
    setSelectedSetting(setting);
    onEditOpen();
  };

  const handleSaveEdit = async () => {
    setSettings((prev) =>
      prev.map((item) => (item.id === selectedSetting.id ? selectedSetting : item))
    );
    let url = `/su/setting/${orgToken}`;
    let data = {
      key: selectedSetting.key,
      value: selectedSetting.value,
      data_type: selectedSetting.data_type,
    };
    await axiosInstance.post(url, data);
    onEditClose();
  };

  const handleAddNew = async () => {
    setSettings((prev) => [
      ...prev,
      {
        ...newSetting,
        audit_data: null,
        created: new Date().toISOString(),
        modified: new Date().toISOString(),
      },
    ]);
    let url = `/su/setting/${orgToken}`;
    let data = {
      key: newSetting.key,
      value: newSetting.value,
      data_type: newSetting.data_type,
    };
    await axiosInstance.post(url, data);
    setNewSetting({ key: '', value: '', data_type: '' });
    onAddClose();
  };

  const handleDelete = async (key) => {
    const result = await Swal.fire({
      title: `Delete setting "${key}"?`,
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;

    try {
      let url = `/su/orgsettings/${orgToken}/${key}`;
      await axiosInstance.delete(url);
      setSettings((prev) => prev.filter((s) => s.key !== key));
      Swal.fire('Deleted!', 'The setting has been deleted.', 'success');
    } catch (err) {
      console.error('Failed to delete setting:', err);
      Swal.fire('Error', 'Failed to delete setting', 'error');
    }
  };

  const truncateText = (text, maxLength = 20) => {
    if (!text) return 'None';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const renderValueInput = (value, onChange, dataType) => {
    const inputType = dataTypes[dataType] || 'text';

    if (inputType === 'checkbox') {
      return (
        <Checkbox
          isChecked={value === 'true' || value === true}
          onChange={(e) =>
            onChange({ target: { value: e.target.checked.toString() } })
          }
        />
      );
    }

    return (
      <Input
        type={inputType}
        value={value}
        onChange={onChange}
        step={
          inputType === 'number' && (dataType === 'float' || dataType === 'double')
            ? 'any'
            : undefined
        }
      />
    );
  };

  return (
    <Box p={5} overflowX="hidden">
      <VStack spacing={4} align="stretch">
        <Text fontSize="2xl" fontWeight="bold">
          Organization Settings
        </Text>
        <PrimaryButton
          alignSelf={'end'}
          w={'fit-content'}
          colorScheme="teal"
          onClick={onAddOpen}
          label={'Add New Setting +'}
        />
        <Box overflowX="auto">
          <Table variant="simple" size="md" width="100%">
            <Thead>
              <Tr>
                <Th w="20%">Key</Th>
                <Th w="25%">Value</Th>
                <Th w="5%">Data Type</Th>
                <Th w="35%">Audit Data</Th>
                <Th w="15%">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {settings.map((setting) => (
                <Tr key={setting.id}>
                  <Td title={setting.key}>{truncateText(setting.key)}</Td>
                  <Td title={setting.value}>{truncateText(setting.value)}</Td>
                  <Td title={setting.data_type}>{truncateText(setting.data_type)}</Td>
                  <Td maxW="35%">
                    {Array.isArray(setting.audit_data) && setting.audit_data.length > 0 ? (
                      <Box>
                        {setting.audit_data.map((entry, index) => (
                          <Box key={index} mb={2}>
                            <Text fontSize="sm">
                              <b>{entry.action}</b> by <i>{entry.email}</i>
                            </Text>
                            <Text fontSize="xs">
                              IP: {entry.ip} |{' '}
                              {new Date(entry.lastUpdated).toLocaleString()}
                            </Text>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        No audit data
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <SecondaryButton
                      label={'Edit'}
                      size="sm"
                      colorScheme="blue"
                      onClick={() => handleEdit(setting)}
                      mr={2}
                    />
                    <Button
                      size="sm"
                      colorScheme="red"

                      onClick={() => handleDelete(setting.key)}
                    >
                      Delete
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </VStack>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Setting</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedSetting && (
              <>
                <FormControl mb={3}>
                  <FormLabel>Key</FormLabel>
                  <Input
                    value={selectedSetting.key}
                    onChange={(e) =>
                      setSelectedSetting({ ...selectedSetting, key: e.target.value })
                    }
                  />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Data Type</FormLabel>
                  <Select
                    value={selectedSetting.data_type || ''}
                    onChange={(e) =>
                      setSelectedSetting({ ...selectedSetting, data_type: e.target.value })
                    }
                  >
                    {Object.keys(dataTypes).map((key) => (
                      <option key={key} value={key}>{key}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel>Value</FormLabel>
                  {renderValueInput(
                    selectedSetting.value,
                    (e) => setSelectedSetting({ ...selectedSetting, value: e.target.value }),
                    selectedSetting.data_type
                  )}
                </FormControl>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <PrimaryButton label={'Save'} colorScheme="blue" mr={3} onClick={handleSaveEdit} />
            <Button variant="ghost" onClick={onEditClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add New Modal */}
      <Modal isOpen={isAddOpen} onClose={onAddClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Setting</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel>Key</FormLabel>
          <Input
                value={newSetting.key}
                onChange={(e) => setNewSetting({ ...newSetting, key: e.target.value })}
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Data Type</FormLabel>
              <Select
                value={newSetting.data_type}
                onChange={(e) => setNewSetting({ ...newSetting, data_type: e.target.value })}
              >
                {Object.keys(dataTypes).map((key) => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </Select>
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Value</FormLabel>
              {renderValueInput(
                newSetting.value,
                (e) => setNewSetting({ ...newSetting, value: e.target.value }),
                newSetting.data_type
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <PrimaryButton label={'Save'} mr={3} onClick={handleAddNew} />
            <Button variant="ghost" onClick={onAddClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
